import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PromoBanner from "@/components/PromoBanner";
import { Button } from "@/components/ui/button";
import { Check, Zap } from "lucide-react";

export function PlanGrid({ compact = false }) {
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [loading, setLoading] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { api.get("/billing/plans").then((r) => setPlans(r.data.plans)).catch(() => {}); }, []);

  const subscribe = async (plan) => {
    if (!user) { navigate("/register"); return; }
    const lookup_key = plan[cycle].lookup_key;
    setLoading(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de paiement");
      setLoading(null);
    }
  };

  return (
    <div>
      <div className="flex justify-center mb-8">
        <div className="inline-flex p-1 rounded-xl bg-secondary" data-testid="billing-cycle-toggle">
          {[{ id: "monthly", l: "Mensuel" }, { id: "yearly", l: "Annuel -33%" }].map((c) => (
            <button key={c.id} onClick={() => setCycle(c.id)} className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${cycle === c.id ? "bg-background shadow mg-glow" : "text-muted-foreground"}`} data-testid={`cycle-${c.id}`}>{c.l}</button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {plans.map((p) => (
          <div key={p.id} className={`relative p-6 rounded-2xl border bg-card flex flex-col ${p.popular ? "border-primary mg-glow" : "border-border"}`} data-testid={`plan-${p.id}`}>
            {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-indigo-500 to-cyan-500 text-white">Le plus populaire</span>}
            <h3 className="font-display font-bold text-xl">{p.name}</h3>
            <p className="text-xs text-muted-foreground h-8">{p.target}</p>
            <div className="my-4">
              <span className="font-display text-4xl font-extrabold">{p[cycle].price}</span>
              <span className="text-muted-foreground text-sm">/{cycle === "monthly" ? "mois" : "an"}</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm font-medium mb-4"><Zap size={15} className="text-amber-400" /> {p.credits.toLocaleString("fr-FR")} crédits IA</div>
            <ul className="space-y-2.5 flex-1 mb-6">
              {p.features.map((f) => <li key={f} className="flex items-start gap-2 text-sm"><Check size={16} className="text-emerald-500 mt-0.5 shrink-0" />{f}</li>)}
            </ul>
            <Button onClick={() => subscribe(p)} disabled={loading === p[cycle].lookup_key || user?.plan === p.id} className={`w-full h-11 rounded-xl ${p.popular ? "bg-gradient-to-r from-indigo-500 to-cyan-500 text-white border-0" : ""}`} variant={p.popular ? "default" : "outline"} data-testid={`subscribe-${p.id}`}>
              {user?.plan === p.id ? "Plan actuel" : loading === p[cycle].lookup_key ? "Redirection…" : "Choisir"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Pricing() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="pt-32 pb-24 px-6 mg-radial">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-semibold tracking-widest uppercase text-primary">Tarifs</span>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold mt-3">Un plan pour chaque ambition</h1>
            <p className="text-muted-foreground mt-4 text-lg">Des crédits IA, tous les réseaux, aucune limite de créativité. Annulez à tout moment.</p>
          </div>
          <div className="max-w-3xl mx-auto mb-10"><PromoBanner /></div>
          <PlanGrid />
        </div>
      </section>
      <Footer />
    </div>
  );
}
